import TransactionsLayer from '../layers/TransactionsLayer';
import { removeSourceAndLayers } from '../utils/maplibre-helpers';
import { resolveFavoritableSelectionContext } from '../utils/favorite-context';

export function createSelectionManager(config) {
    var state = config.state;

    function getSelectedListingId() {
        return typeof state.getSelectedListingId === 'function' ? state.getSelectedListingId() : null;
    }

    function setSelectedListingId(value) {
        if (typeof state.setSelectedListingId === 'function') {
            state.setSelectedListingId(value);
        }
    }

    function selectionActive() {
        return !!(
            state.getSelectedTid()
            || state.getSelectedDid()
            || state.getSelectedBid()
            || state.getSelectedGunbId()
            || getSelectedListingId()
            || state.getSelectedOtoczenieFeature()
            || state.getSelectedSignalContext()
        );
    }

    function updateSelectionControl() {
        var active = selectionActive();
        var signalContext = state.getSelectedSignalContext();
        var hasFocusedSignal = !!(signalContext && signalContext.selectedSignalId);
        if (typeof config.syncListingMarkerSelection === 'function') {
            config.syncListingMarkerSelection(getSelectedListingId());
        }
        if (config.floatingPillbarEl) config.floatingPillbarEl.hidden = !active;
        if (config.personaBtn) {
            config.personaBtn.disabled = state.getSelectedLatLng() == null;
        }
        if (!config.selectionBannerEl) return;

        config.selectionBannerEl.hidden = !active;
        if (!active) return;

        if (config.selectionBannerTextEl) {
            if (state.getSelectedGunbId()) {
                config.selectionBannerTextEl.textContent = 'Masz wybrane pozwolenie na budowę. Kliknij "Odznacz", aby je odznaczyć i wrócić do listy.';
            } else if (getSelectedListingId()) {
                config.selectionBannerTextEl.textContent = 'Masz wybrane ogłoszenie nieruchomości. Kliknij "Odznacz", aby je odznaczyć i wrócić do listy.';
            } else if (state.getSelectedDid()) {
                config.selectionBannerTextEl.textContent = 'Masz wybraną działkę na mapie. Kliknij "Odznacz", aby ją odznaczyć i wrócić do listy.';
            } else if (state.getSelectedBid()) {
                config.selectionBannerTextEl.textContent = 'Masz wybrany budynek na mapie. Kliknij "Odznacz", aby go odznaczyć i wrócić do listy.';
            } else if (state.getSelectedOtoczenieFeature()) {
                config.selectionBannerTextEl.textContent = 'Masz wybrany obiekt z otoczenia. Kliknij "Odznacz", aby go odznaczyć i wrócić do listy.';
            } else if (state.getSelectedSignalContext()) {
                config.selectionBannerTextEl.textContent = hasFocusedSignal
                    ? 'Masz wybrany sygnał społeczności. Kliknij "Odznacz", aby go odznaczyć i wrócić do listy.'
                    : 'Masz wybrane sygnały społeczności dla tego miejsca. Kliknij "Odznacz", aby je odznaczyć i wrócić do listy.';
            } else {
                config.selectionBannerTextEl.textContent = 'Masz wybraną transakcję na mapie. Kliknij "Odznacz", aby ją odznaczyć i wrócić do listy.';
            }
        }

        if (config.selectionClearBtn) {
            var ariaLabel = state.getSelectedGunbId()
                ? 'Odznacz pozwolenie'
                : (getSelectedListingId())
                    ? 'Odznacz ogłoszenie'
                    : (state.getSelectedDid())
                        ? 'Odznacz działkę'
                        : (state.getSelectedBid())
                            ? 'Odznacz budynek'
                            : (state.getSelectedOtoczenieFeature())
                                ? 'Odznacz obiekt z otoczenia'
                                : (state.getSelectedSignalContext())
                                    ? (hasFocusedSignal ? 'Odznacz sygnał społeczności' : 'Odznacz sygnały społeczności')
                            : 'Odznacz transakcję';
            config.selectionClearBtn.setAttribute('aria-label', ariaLabel);
        }

        if (typeof config.onSelectionChanged === 'function') {
            config.onSelectionChanged(buildFavoritableContext(state));
        }
    }

    function buildFavoritableContext(state) {
        // `selectedDid` / `selectedBid` carry PRG external identifiers
        // (id_dzialki / id_budynku), not the internal `egib_dzialki.id` /
        // `egib_budynki.id` primary keys. The server resolves externally.
        return resolveFavoritableSelectionContext({
            selectedTid: state.getSelectedTid(),
            selectedListingId: getSelectedListingId(),
            selectedDid: state.getSelectedDid(),
            selectedBid: state.getSelectedBid(),
            // Signals store the internal miejscowosc.id directly.
            selectedSignalContext: state.getSelectedSignalContext(),
        });
    }

    function resetHighlight() {
        state.setHighlightedLayer(null);
    }

    function applySelectionFocusState() {
        var highlightedLayer = null;

        if (!selectionActive()) {
            resetHighlight();
            config.updateParcelLabels();
            updateSelectionControl();
            return null;
        }

        if (state.getSelectedGunbId() && state.getSelectedGunbLayer()) {
            highlightedLayer = state.getSelectedGunbLayer();
        } else if ((state.getSelectedDid() || state.getSelectedBid()) && state.getSelectedFeatureLayer()) {
            highlightedLayer = state.getSelectedFeatureLayer();
        }

        state.setHighlightedLayer(highlightedLayer);
        config.updateParcelLabels();
        updateSelectionControl();

        return highlightedLayer;
    }

    function clearTransactionHighlight() {
        var transactionsController = config.getTransactionsController();
        if (transactionsController && typeof transactionsController.clearHighlight === 'function') {
            transactionsController.clearHighlight();
        } else if (config.getMap()) {
            removeSourceAndLayers(config.getMap(), TransactionsLayer.HIGHLIGHT_SOURCE_ID, TransactionsLayer.HIGHLIGHT_LAYER_IDS);
        }
    }

    function highlightTransactionById(transactionId) {
        var latestGeoData = config.getLatestGeoData();
        if (!transactionId || !state.getGeoLayer() || (latestGeoData && latestGeoData.aggregated)) return false;

        applySelectionFocusState();

        var selectedLatLng = state.getSelectedLatLng();
        var rowCoords = state.getRowCoords() || {};
        if (!selectedLatLng && rowCoords[transactionId]) {
            selectedLatLng = {
                lat: rowCoords[transactionId].lat,
                lng: rowCoords[transactionId].lng,
            };
            state.setSelectedLatLng(selectedLatLng);
        }

        var transactionsController = config.getTransactionsController();
        var result = transactionsController
            ? transactionsController.highlightFeatureById(transactionId, latestGeoData)
            : { found: false, center: null };

        if (result.found) {
            config.bringDataLayersToFront();
            if (result.center) {
                state.setSelectedLatLng(result.center);
                config.rebuildNearbyLinkIfNeeded();
            }
        }

        if (state.getPendingNearbyRestore() && state.getSelectedLatLng() && !state.getNearbyLoaded()) {
            config.loadNearbyForSelection(true);
        }

        return result.found;
    }

    function clearSelection(options) {
        options = options || {};

        state.setSelectedTid(null);
        state.setSelectedDid(null);
        state.setSelectedBid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedSignalContext(null);
        config.clearTapBrowseState();
        state.setSelectedLatLng(null);
        state.setSelectedTransactionRow(null);
        state.setPendingNearbyRestore(false);

        resetHighlight();
        clearTransactionHighlight();
        config.clearSignalFocus();
        config.clearSelectedFeatureVisual();
        config.clearSelectedGunbVisual();
        config.clearNearbyState();
        if (!options.keepDetails) config.renderDetailsPlaceholder();

        config.applySelectedStylesInList();
        config.applySelectedStylesInGunbList();
        updateSelectionControl();
        config.bringDataLayersToFront();

        if (options.sync !== false) config.syncUrl();
    }

    function getRowLatLng(row) {
        if (!row || row.lat == null || row.lng == null) return null;
        return { lat: parseFloat(row.lat), lng: parseFloat(row.lng) };
    }

    function selectTransactionByRow(row, shouldFly) {
        var map = config.getMap();
        if (!row) return;

        state.setPendingNearbyRestore(false);
        config.clearSignalFocus();
        state.setSelectedTransactionRow(row);
        state.setSelectedTid(row.tran_lokalny_id_iip);
        state.setSelectedDid(null);
        state.setSelectedBid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedSignalContext(null);
        config.clearTapBrowseState();
        state.setSelectedLatLng(getRowLatLng(row) || state.getSelectedLatLng());
        config.clearSelectedFeatureVisual();
        config.clearSelectedGunbVisual();
        config.clearNearbyState();
        config.renderDetailsLoading('Transakcja ' + row.tran_lokalny_id_iip.substring(0, 8));
        config.fetchTransactionDetail(row.tran_lokalny_id_iip);
        highlightTransactionById(row.tran_lokalny_id_iip);
        config.applySelectedStylesInList();
        config.applySelectedStylesInGunbList();

        if (shouldFly !== false && state.getSelectedLatLng() && map) {
            map.flyTo({
                center: [state.getSelectedLatLng().lng, state.getSelectedLatLng().lat],
                zoom: Math.max(map.getZoom(), config.INDIVIDUAL_ZOOM_THRESHOLD),
                duration: 450,
                padding: config.getMapPaddingForPanel()
            });
        }

        config.setTab('details', { snap: 'half', sync: false });
        config.syncUrl();
    }

    function selectTransactionByFeature(props, latlng) {
        if (!props || !props.tran_lokalny_id_iip) return;

        state.setPendingNearbyRestore(false);
        config.clearSignalFocus();
        state.setSelectedTid(props.tran_lokalny_id_iip);
        state.setSelectedDid(null);
        state.setSelectedBid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedSignalContext(null);
        config.clearTapBrowseState();
        state.setSelectedLatLng(latlng || state.getSelectedLatLng());
        state.setSelectedTransactionRow((state.getListRows() || []).find(function(row) {
            return row.tran_lokalny_id_iip === props.tran_lokalny_id_iip;
        }) || state.getSelectedTransactionRow());
        config.clearSelectedFeatureVisual();
        config.clearSelectedGunbVisual();
        config.clearNearbyState();
        config.renderDetailsLoading('Transakcja ' + props.tran_lokalny_id_iip.substring(0, 8));
        config.fetchTransactionDetail(props.tran_lokalny_id_iip);
        highlightTransactionById(props.tran_lokalny_id_iip);
        config.applySelectedStylesInList();
        config.applySelectedStylesInGunbList();
        config.setTab('details', { snap: 'half', sync: false });
        config.easeMapToKeepPointVisible(state.getSelectedLatLng());
        config.syncUrl();
    }

    function selectParcel(props, latlng, options) {
        if (!props || !props.id_dzialki) return;

        options = options || {};

        var parcelId = String(props.id_dzialki);
        var nextFeatureKey = config.getSelectedFeatureKey('parcel', parcelId);
        var hasDetails = config.hasParcelDetails(props);
        var signalsContext = options.signalsContext || null;

        state.setPendingNearbyRestore(false);
        config.clearSignalFocus();
        state.setSelectedTid(null);
        state.setSelectedBid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedDid(parcelId);
        state.setSelectedSignalContext(signalsContext);
        config.clearTapBrowseState();
        clearTransactionHighlight();
        state.setSelectedLatLng(latlng || state.getSelectedLatLng());
        state.setSelectedTransactionRow(null);
        resetHighlight();
        config.clearSelectedGunbVisual();

        if (state.getSelectedFeatureLayerKey() && state.getSelectedFeatureLayerKey() !== nextFeatureKey) {
            config.clearSelectedFeatureVisual();
        }

        config.clearNearbyState();

        if (hasDetails) {
            config.renderParcelDetails(props, { signalsContext: signalsContext });
        } else {
            config.renderDetailsLoading('Dzia\u0142ka ' + (props.numer || props.id_dzialki || ''));
        }

        config.ensureSelectedFeatureData('parcel', parcelId, {
            renderLayer: true,
            renderDetails: true
        });
        applySelectionFocusState();
        config.applySelectedStylesInGunbList();
        config.setTab('details', { snap: 'half', sync: false });
        config.easeMapToKeepPointVisible(state.getSelectedLatLng());
        config.syncUrl();
    }

    function selectBuilding(props, latlng, options) {
        if (!props || !props.id_budynku) return;

        options = options || {};

        var buildingId = String(props.id_budynku);
        var nextFeatureKey = config.getSelectedFeatureKey('building', buildingId);
        var hasDetails = config.hasBuildingDetails(props);
        var signalsContext = options.signalsContext || null;

        state.setPendingNearbyRestore(false);
        config.clearSignalFocus();
        state.setSelectedTid(null);
        state.setSelectedDid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedBid(buildingId);
        state.setSelectedSignalContext(signalsContext);
        config.clearTapBrowseState();
        clearTransactionHighlight();
        state.setSelectedLatLng(latlng || state.getSelectedLatLng());
        state.setSelectedTransactionRow(null);
        resetHighlight();
        config.clearSelectedGunbVisual();

        if (state.getSelectedFeatureLayerKey() && state.getSelectedFeatureLayerKey() !== nextFeatureKey) {
            config.clearSelectedFeatureVisual();
        }

        config.clearNearbyState();

        if (hasDetails) {
            config.renderBuildingDetails(props, { signalsContext: signalsContext });
        } else {
            config.renderDetailsLoading('Budynek ' + (props.id_budynku || ''));
        }

        config.ensureSelectedFeatureData('building', buildingId, {
            renderLayer: true,
            renderDetails: true
        });
        applySelectionFocusState();
        config.applySelectedStylesInGunbList();
        config.setTab('details', { snap: 'half', sync: false });
        config.easeMapToKeepPointVisible(state.getSelectedLatLng());
        config.syncUrl();
    }

    function selectSignalHit(props, latlng, signalId) {
        if (!props || props.entity_id == null || !props.target_type) {
            return;
        }

        state.setPendingNearbyRestore(false);
        var resolvedSignalId = signalId != null && signalId !== ''
            ? String(signalId)
            : (Number(props.signal_count || 0) === 1 && props.id != null ? String(props.id) : null);

        var signalsContext = {
            entityType: String(props.target_type),
            entityId: String(props.entity_id),
            targetIdentifier: props.target_identifier != null ? String(props.target_identifier) : null,
            targetLabel: props.target_label || null,
            targetSubtitle: props.target_subtitle || null,
            selectedSignalId: resolvedSignalId,
            // Wejście przez sygnał — panel ma pokazać sygnały przed danymi obiektu.
            signalFirst: true,
        };

        if (signalsContext.entityType === 'dzialka' && signalsContext.targetIdentifier) {
            selectParcel({
                id_dzialki: signalsContext.targetIdentifier,
                numer: props.target_label || null,
            }, latlng, {
                signalsContext: signalsContext,
            });
            return;
        }

        if (signalsContext.entityType === 'budynek' && signalsContext.targetIdentifier) {
            selectBuilding({
                id_budynku: signalsContext.targetIdentifier,
            }, latlng, {
                signalsContext: signalsContext,
            });
            return;
        }

        config.clearSignalFocus();
        state.setSelectedTid(null);
        state.setSelectedDid(null);
        state.setSelectedBid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(null);
        state.setSelectedSignalContext(signalsContext);
        config.clearTapBrowseState();
        clearTransactionHighlight();
        state.setSelectedLatLng(latlng || state.getSelectedLatLng());
        state.setSelectedTransactionRow(null);
        resetHighlight();
        config.clearSelectedFeatureVisual();
        config.clearSelectedGunbVisual();
        config.clearNearbyState();
        config.renderSignalTargetDetails(props, signalsContext);
        config.loadSignalsForContext(signalsContext);
        config.applySelectedStylesInList();
        config.applySelectedStylesInGunbList();
        applySelectionFocusState();
        config.setTab('details', { snap: 'half', sync: false });
        config.easeMapToKeepPointVisible(state.getSelectedLatLng());
        config.syncUrl();
    }


    function selectOtoczenieFeature(feature, latlng) {
        var resolvedLatLng = latlng || (feature ? config.getFeatureCentroid(feature) : null) || state.getSelectedLatLng();
        if (!feature || !resolvedLatLng) {
            return;
        }

        state.setPendingNearbyRestore(false);
        config.clearSignalFocus();
        state.setSelectedTid(null);
        state.setSelectedDid(null);
        state.setSelectedBid(null);
        state.setSelectedGunbId(null);
        setSelectedListingId(null);
        state.setSelectedOtoczenieFeature(feature);
        state.setSelectedSignalContext(null);
        config.clearTapBrowseState();
        clearTransactionHighlight();
        state.setSelectedLatLng(resolvedLatLng);
        state.setSelectedTransactionRow(null);
        resetHighlight();
        config.clearSelectedFeatureVisual();
        config.clearSelectedGunbVisual();
        config.clearNearbyState();
        config.renderOtoczenieDetails(feature);
        config.applySelectedStylesInList();
        config.applySelectedStylesInGunbList();
        applySelectionFocusState();
        config.setTab('details', { snap: 'half', sync: false });
        config.easeMapToKeepPointVisible(state.getSelectedLatLng());
        config.syncUrl();
    }

    function runClearSelectionFromBanner() {
        var activeTab = state.getActiveTab();
        if (activeTab === 'details' || activeTab === 'nearby') {
            config.setTab('list', { sync: false });
        }
        clearSelection({ keepDetails: false });
    }

    return {
        applySelectionFocusState: applySelectionFocusState,
        clearSelection: clearSelection,
        clearTransactionHighlight: clearTransactionHighlight,
        getRowLatLng: getRowLatLng,
        highlightTransactionById: highlightTransactionById,
        resetHighlight: resetHighlight,
        runClearSelectionFromBanner: runClearSelectionFromBanner,
        selectBuilding: selectBuilding,
        selectOtoczenieFeature: selectOtoczenieFeature,
        selectParcel: selectParcel,
        selectSignalHit: selectSignalHit,
        selectionActive: selectionActive,
        selectTransactionByFeature: selectTransactionByFeature,
        selectTransactionByRow: selectTransactionByRow,
        updateSelectionControl: updateSelectionControl,
    };
}
